import {
    IonApp,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonMenu,
    IonMenuToggle,
    IonNote,
    IonRouterOutlet,
    IonTitle,
    IonToolbar,
    setupIonicReact
} from '@ionic/react';
import {Route, useHistory, useLocation} from 'react-router-dom';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
import {
    bookSharp, calendar,
    cardOutline, dice,
    flame,
    gameController,
    homeOutline,
    logOut,
    person,
    power,
    trophy
} from "ionicons/icons";
import PersonalStats from "./pages/stats/PersonalStats";
import React, {useCallback, useEffect, useState} from "react";
import Login from "./pages/auth/Login";
import DertigenList from "./pages/dertigen/DertigenList";
import DertigenLobbyList from "./pages/dertigen/DertigenLobbyList";
import AdminOverview from "./pages/admin/AdminOverview";
import Ranglijsten from "./pages/stats/Ranglijsten";
import AdminPushNotifications from "./pages/admin/AdminPushNotifications";

import './App.css'
import AdminPersonalOverview from "./pages/admin/AdminPersonalOverview";
import PrivateRoute from "./components/shared/PrivateRoute";
import Transactions from "./pages/transactions/Transactions";
import Register from "./pages/auth/Register";
import PresenceList from "./pages/presence/PresenceList";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import TransactionSuccesful from "./pages/transactions/TransactionSuccesful";
import TransactionUnsuccessful from "./pages/transactions/TransactionUnsuccessful";
import ProfilePicture from "./components/shared/ProfilePicture";
import ResetPasswordEmailSend from "./pages/auth/ResetPasswordEmailSend";
import RequestPasswordResetPage from "./pages/auth/RequestPasswordResetPage";
import ProfilePage from "./pages/profile/ProfilePage";
import MqttController from "./pages/iot/MqttController";
import MqttSceneOverview from "./pages/iot/scenes/MqttSceneOverview";
import YoloDiceCam from "./components/iot/yolo_dice_cam/YoloDiceCam";
import Evenings from "./pages/presence/Evenings";
import UpdateAvailableButton from "./components/shared/UpdateAvailableButton";
import {applyUpdate} from "./serviceWorkerRegistration";
import {ACCOUNT_ROLES, MENU_MEMBER_ROLES, ROLES, hasAnyRole, hasRole} from "./auth/rbac";
import {useAppDispatch, useAppSelector} from "./store/hooks";
import {clearAuth, initializeSession} from "./store/slices/authSlice";
import {clearUserProfile} from "./store/slices/userSlice";
import {syncPushNotificationsIfEnabled, unlinkPushNotificationsForCurrentUser} from "./services/pushNotifications";
import {formatReleaseTimestamp, useAppRelease} from "./services/appRelease";
import UpdatesPage from "./pages/updates/UpdatesPage";
import {refreshOutline} from "ionicons/icons";

setupIonicReact();

const App: React.FC = () => {
    const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.user.profile);
    const {
        installedRelease,
        latestRelease,
        isUpdateAvailable,
        refreshReleaseInfo,
    } = useAppRelease();

    const [activePage, setActivePage] = useState<string>('index');

    const location = useLocation();
    const history = useHistory()

    const pathToStateMap: { [key: string]: string } = {
        '/': 'index',
        '/dertigen': 'dertigen',
        '/transactions': 'transactions',
        '/ranglijsten': 'ranking',
        '/aanwezigheid': 'presence',
        '/updates': 'updates',
        '/admin': 'admin',
        '/statutes': 'statuten',
        '/account-settings': 'account-settings',
        '/firewall-rules': 'firewall-rules',
        '/mqtt-controller': 'mqtt-controller',
        '/scenes': 'scenes',
        '/yolo-dice-cam': 'yolo-dice-cam'
    };

    useEffect(() => {
        const pathSegments = location.pathname.split('/').filter(Boolean);

        const firstSegment = pathSegments.length > 0 ? `/${pathSegments[0]}` : 'index';

        setActivePage(pathToStateMap[firstSegment] || 'index');
    }, [location]);

    useEffect(() => {
        dispatch(initializeSession());
    }, [dispatch])

    useEffect(() => {
        if (!user?.id) {
            return;
        }

        syncPushNotificationsIfEnabled().catch((error) => {
            console.warn('[Push] Token sync failed.', error);
        });
    }, [user?.id]);

    // Listen for service worker update events dispatched from index.tsx
    useEffect(() => {
        const handleSwUpdate = (e: Event) => {
            const reg = (e as CustomEvent<ServiceWorkerRegistration>).detail;
            setSwRegistration(reg);
            refreshReleaseInfo().catch((error) => {
                console.warn('[Release] Release metadata herladen na SW update mislukt.', error);
            });
        };
        document.addEventListener('swUpdate', handleSwUpdate);
        return () => document.removeEventListener('swUpdate', handleSwUpdate);
    }, [refreshReleaseInfo]);

    async function removeJwtTokenAndRedirect() {
        try {
            await unlinkPushNotificationsForCurrentUser();
        } catch (error) {
            console.warn('[Push] Could not unlink push token during logout.', error);
        }

        dispatch(clearAuth());
        dispatch(clearUserProfile());
        redirectTo('/login')
    }

    function closeAppMenu() {
        const menu = document.querySelector('ion-menu');
        menu?.close();
    }

    function redirectTo(path: string) {
        history.push(path)
        closeAppMenu();
    }

    const userRoles = user?.roles;
    const canAccessMemberMenu = hasAnyRole(userRoles, MENU_MEMBER_ROLES);
    const canAccessAccountSettings = hasAnyRole(userRoles, ACCOUNT_ROLES);
    const isAdmin = hasRole(userRoles, ROLES.ADMIN);
    const canApplyServiceWorkerUpdate = Boolean(swRegistration?.waiting);
    const shouldShowUpdatePrompt = canApplyServiceWorkerUpdate || isUpdateAvailable;

    const handleUpdateAction = useCallback(async () => {
        const registration = swRegistration ?? await navigator.serviceWorker.getRegistration();

        if (registration?.waiting) {
            applyUpdate(registration);
            return;
        }

        try {
            await registration?.update();
            // update() can finish before installation does; the central
            // registration listener will surface the prompt once it is waiting.
            setSwRegistration(registration ?? null);
            await refreshReleaseInfo();
        } catch (error) {
            console.warn('[SW] Handmatige updatecheck mislukt.', error);
        }
    }, [refreshReleaseInfo, swRegistration]);

    return (
        <IonApp>
            <IonRouterOutlet id="main-content">
                <Route exact path="/login">
                    <Login/>
                </Route>
                <Route exact path="/register">
                    <Register/>
                </Route>
                <Route exact path="/reset-password/:token">
                    <ResetPasswordPage/>
                </Route>
                <Route exact path="/reset-password-email-send">
                    <ResetPasswordEmailSend/>
                </Route>
                <Route exact path="/request-password-reset">
                    <RequestPasswordResetPage/>
                </Route>
                <PrivateRoute exact path="/welkom"
                              component={PersonalStats}/>
                <PrivateRoute exact path="/presence/:userId"
                              component={PresenceList}/>
                <PrivateRoute exact path="/dertigen"
                              component={DertigenLobbyList}/>
                <PrivateRoute exact path="/dertigen/:lobbyId"
                              component={DertigenList}/>
                <PrivateRoute exact path="/"
                              component={PersonalStats}/>
                <PrivateRoute exact path="/admin"
                              component={AdminOverview}
                              requiredRoles={[ROLES.ADMIN]}/>
                <PrivateRoute exact path="/admin/push-notifications"
                              component={AdminPushNotifications}
                              requiredRoles={[ROLES.ADMIN]}/>
                <PrivateRoute exact path="/admin/:playerId(\d+)"
                              component={AdminPersonalOverview}
                              requiredRoles={[ROLES.ADMIN]}/>
                <PrivateRoute exact path="/transactions"
                              component={Transactions}/>
                <PrivateRoute exact path="/ranglijsten"
                              component={Ranglijsten}/>
                <PrivateRoute exact path="/successful_payment"
                              component={TransactionSuccesful}/>
                <PrivateRoute exact path="/cancelled_payment"
                              component={TransactionUnsuccessful}/>
                <PrivateRoute exact path="/account-settings" component={ProfilePage}/>
                <PrivateRoute exact path="/updates" component={UpdatesPage}/>
                <PrivateRoute exact path="/iot-controller" component={MqttController} requiredRoles={[ROLES.ADMIN]}/>
                <PrivateRoute exact path="/scenes" component={MqttSceneOverview} requiredRoles={[ROLES.ADMIN]}/>
                <PrivateRoute exact path="/yolo-dice-cam" component={YoloDiceCam} requiredRoles={[ROLES.ADMIN]}/>
                <PrivateRoute exact path="/aanwezigheid" component={Evenings}/>
            </IonRouterOutlet>
            <IonMenu side="start" contentId="main-content">
                <IonHeader>
                    <IonToolbar>
                        <div className={'sidebar-toolbar'}>
                            <IonTitle>{user?.name}</IonTitle>
                            <ProfilePicture
                                profilePicUrl={(user?.profilePictureUrl?.length !== 0 && user?.profilePictureUrl) ? `${process.env.REACT_APP_STORAGE}${user.profilePictureUrl}` : `${process.env.REACT_APP_STORAGE}default-profile.jpg`}
                                showEditable={false} width={30} height={30} fullName={user?.name ? user?.name : 'A'}/>
                        </div>
                    </IonToolbar>
                </IonHeader>
                <IonContent class={'sidebar-content'}>
                    <IonList lines={'none'}>
                        <IonMenuToggle auto-hide="false">
                            <IonItem button onClick={() => redirectTo("/")} className={activePage === 'index' ? 'selected' : 'not-selected'}>
                                <IonIcon slot="start" icon={person}/>
                                <IonLabel>Info</IonLabel>
                            </IonItem>
                        </IonMenuToggle>
                        <IonMenuToggle auto-hide="false">
                            <IonItem button onClick={() => redirectTo("/dertigen")}
                                     className={activePage === 'dertigen' ? 'selected' : 'not-selected'}>
                                <IonIcon slot="start" icon={gameController}/>
                                <IonLabel>Dertigen</IonLabel>
                            </IonItem>
                        </IonMenuToggle>
                        {canAccessMemberMenu && (
                            <IonMenuToggle auto-hide="false">
                                <IonItem button onClick={() => redirectTo("/transactions")}
                                         className={activePage === 'transactions' ? 'selected' : 'not-selected'}>
                                    <IonIcon slot="start" icon={cardOutline}/>
                                    <IonLabel>Transacties</IonLabel>
                                </IonItem>
                            </IonMenuToggle>
                        )}
                        {canAccessMemberMenu && (
                            <IonMenuToggle auto-hide="false">
                                <IonItem button onClick={() => redirectTo("/ranglijsten")}
                                         className={activePage === 'ranking' ? 'selected' : 'not-selected'}>
                                    <IonIcon slot="start" icon={trophy}/>
                                    <IonLabel>Ranglijsten</IonLabel>
                                </IonItem>
                            </IonMenuToggle>
                        )}
                        {canAccessMemberMenu && (
                            <IonMenuToggle auto-hide="false">
                                <IonItem button onClick={() => redirectTo("/aanwezigheid")}
                                         className={activePage === 'presence' ? 'selected' : 'not-selected'}>
                                    <IonIcon slot="start" icon={calendar}/>
                                    <IonLabel>Aanwezige avonden</IonLabel>
                                </IonItem>
                            </IonMenuToggle>
                        )}
                        {canAccessAccountSettings && (
                            <IonMenuToggle auto-hide="false">
                                <IonItem button onClick={() => redirectTo("/account-settings")}
                                         className={activePage === 'account-settings' ? 'selected' : 'not-selected'}>
                                    <IonIcon slot="start" icon={person}/>
                                    <IonLabel>Mijn account</IonLabel>
                                </IonItem>
                            </IonMenuToggle>
                        )}
                        <IonMenuToggle auto-hide="false">
                            <IonItem button onClick={() => redirectTo("/updates")}
                                     className={activePage === 'updates' ? 'selected' : 'not-selected'}>
                                <IonIcon slot="start" icon={refreshOutline}/>
                                <IonLabel>Updates</IonLabel>
                                {shouldShowUpdatePrompt && (
                                    <IonNote slot="end" color="warning">Nieuw</IonNote>
                                )}
                            </IonItem>
                        </IonMenuToggle>
                        {isAdmin &&
                            <IonMenuToggle auto-hide="false">
                                <IonItem button onClick={() => redirectTo("/iot-controller")}
                                         className={activePage === 'mqtt-controller' ? 'selected' : 'not-selected'}>
                                    <IonIcon slot="start" icon={gameController}/>
                                    <IonLabel>Verlichting besturen</IonLabel>
                                </IonItem>
                            </IonMenuToggle>
                        }
                        {isAdmin &&
                            <IonMenuToggle auto-hide="false">
                                <IonItem button onClick={() => redirectTo("/scenes")}
                                         className={activePage === 'scenes' ? 'selected' : 'not-selected'}>
                                    <IonIcon slot="start" icon={homeOutline }/>
                                    <IonLabel>Scene's beheren</IonLabel>
                                </IonItem>
                            </IonMenuToggle>
                        }
                        {isAdmin &&
                            <IonMenuToggle auto-hide="false">
                                <IonItem button onClick={() => redirectTo("/admin")}
                                         className={activePage === 'admin' ? 'selected' : 'not-selected'}>
                                    <IonIcon slot="start" icon={power}/>
                                    <IonLabel>Admin</IonLabel>
                                </IonItem>
                            </IonMenuToggle>
                        }
                        {isAdmin &&
                            <IonMenuToggle auto-hide="false">
                                <IonItem button onClick={() => redirectTo("/yolo-dice-cam")}
                                         className={activePage === 'yolo-dice-cam' ? 'selected' : 'not-selected'}>
                                    <IonIcon slot="start" icon={dice}/>
                                    <IonLabel>Yolo Dobbelstenen Test</IonLabel>
                                </IonItem>
                            </IonMenuToggle>
                        }
                        <div className={'logout-button-container'}>
                            <IonItem button onClick={() => removeJwtTokenAndRedirect()}>
                                <IonIcon slot="start" icon={logOut}/>
                                <IonLabel>Log uit</IonLabel>
                            </IonItem>
                        </div>
                    </IonList>
                </IonContent>
            </IonMenu>
            <UpdateAvailableButton
                isVisible={shouldShowUpdatePrompt}
                onUpdate={handleUpdateAction}
                installedVersionLabel={installedRelease ? `${installedRelease.version} • ${formatReleaseTimestamp(installedRelease.buildTime)}` : undefined}
                availableVersionLabel={latestRelease ? `${latestRelease.version} • ${formatReleaseTimestamp(latestRelease.buildTime)}` : undefined}
                canApplyUpdate={canApplyServiceWorkerUpdate}
                message={canApplyServiceWorkerUpdate ? 'Je update staat klaar' : 'Er is een nieuwe frontendversie'}
            />
        </IonApp>
    );
}

export default App;
